import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTransactions } from '../../hooks/useTransactions';
import type { Transaction, TransactionPayload } from '../../hooks/useTransactions';
import { useAccounts } from '../../hooks/useAccounts';
import { useCards } from '../../hooks/useCards';
import { useCategories } from '../../hooks/useCategories';
import { usePayees } from '../../hooks/usePayees';
import { usePlanLimits } from '../../hooks/usePlanLimits';
import { PlanLimitAlert } from '../../components/feature/PlanLimitAlert';
import { UpgradeModal } from '../../components/feature/UpgradeModal';
import { TransactionModal } from './components/TransactionModal';
import { CategoryManager } from './components/CategoryManager';
import { PaymentDetailModal } from './components/PaymentDetailModal';
import { PAYMENT_METHOD_LABELS, PAYMENT_METHOD_MODAL_OPTIONS } from '../../lib/paymentMethods';

import { useTransfers } from '../../hooks/useTransfers';

// ─── Constants ────────────────────────────────────────────────────────────────
const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  pago:      { label: 'Pago',      className: 'bg-emerald-50 text-emerald-700' },
  recebido:  { label: 'Recebido',  className: 'bg-emerald-50 text-emerald-700' },
  pendente:  { label: 'Pendente',  className: 'bg-amber-50 text-amber-700' },
  vencido:   { label: 'Vencido',   className: 'bg-red-50 text-red-700' },
  atrasado:  { label: 'Atrasado',  className: 'bg-red-50 text-red-700' },
};

const MONTH_NAMES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

type StatusFilter = 'pago' | 'recebido' | 'pendente' | 'vencido' | 'atrasado' | null;

const formatCurrency = (val: number) =>
  val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// ─── Safe month addition (avoids overflow, e.g. Jan 31 + 1 month = Feb 28, not Mar 3) ──────────
const addMonthsSafe = (baseDate: Date, months: number): string => {
  const day = baseDate.getDate();
  const result = new Date(baseDate.getFullYear(), baseDate.getMonth() + months, 1);
  const maxDay = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
  result.setDate(Math.min(day, maxDay));
  return result.toISOString().split('T')[0];
};

// ─── Toast ────────────────────────────────────────────────────────────────────
interface ToastState { message: string; type: 'success' | 'error' }

export default function LancamentosPage() {
  const now = new Date();

  // ─── Filters ────────────────────────────────────────────────────────────────
  const [filterMonth,         setFilterMonth]         = useState(now.getMonth() + 1);
  const [filterYear,          setFilterYear]          = useState(now.getFullYear());
  const [filterType,          setFilterType]          = useState<'receita' | 'despesa' | null>(null);
  const [filterStatus,        setFilterStatus]        = useState<StatusFilter>(null);
  const [filterCategory,      setFilterCategory]      = useState<string | null>(null);
  const [filterAccount,       setFilterAccount]       = useState<string | null>(null);
  const [filterPaymentMethod, setFilterPaymentMethod] = useState<string | null>(null);
  const [filterPayee,         setFilterPayee]         = useState<string | null>(null);
  const [search,              setSearch]              = useState('');
  const [showAdvanced,        setShowAdvanced]        = useState(false);

  // ─── Data ────────────────────────────────────────────────────────────────────
  const {
    transactions, loading, error, monthlyIncome, monthlyExpenses,
    realizedIncome, realizedExpenses,
    pendingExpenses, pendingIncome,
    createTransaction, createManyTransactions, updateTransaction,
    deleteTransaction, duplicateTransaction, toggleStatus,
  } = useTransactions({ month: filterMonth, year: filterYear, type: filterType ?? undefined });
  const { accounts, totalBalance } = useAccounts();
  const { cards } = useCards();
  const { categories } = useCategories();
  const { activePayees } = usePayees();
  const { checkTransactionLimit, isPaid, monthlyTransactionLimit } = usePlanLimits();
  const { deleteTransfer } = useTransfers();

  // ─── Lookup maps ──────────────────────────────────────────────────────────────
  const accountMap = useMemo(
    () => Object.fromEntries(accounts.map((a) => [a.id, a])),
    [accounts]
  );
  const cardMap = useMemo(
    () => Object.fromEntries(cards.map((c) => [c.id, c])),
    [cards]
  );

  // ─── UI State ────────────────────────────────────────────────────────────────
  const [showAccountStrip, setShowAccountStrip] = useState(false);
  const [showModal,        setShowModal]        = useState(false);
  const [editTransaction,  setEditTransaction]  = useState<Transaction | null>(null);
  const [showUpgrade,      setShowUpgrade]      = useState(false);
  const [showCatManager,   setShowCatManager]   = useState(false);
  const [deleteConfirm,    setDeleteConfirm]    = useState<string | null>(null);
  const [deleteTransferConfirm, setDeleteTransferConfirm] = useState<string | null>(null);
  const [transactionCount, setTransactionCount] = useState(0);
  const [toast,            setToast]            = useState<ToastState | null>(null);
  const [actionLoading,    setActionLoading]    = useState<string | null>(null);
  const [isCheckingLimit,  setIsCheckingLimit]  = useState(false);
  const [pendingPaymentTx, setPendingPaymentTx] = useState<Transaction | null>(null);
  const [paymentDetailLoading, setPaymentDetailLoading] = useState(false);

  const showToast = useCallback((message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const refreshCount = useCallback(async () => {
    const check = await checkTransactionLimit();
    setTransactionCount(check.current);
  }, [checkTransactionLimit]);

  useEffect(() => { refreshCount(); }, [refreshCount, transactions]);

  // ─── Month navigation ─────────────────────────────────────────────────────────
  const goToPrevMonth = () => {
    if (filterMonth === 1) { setFilterMonth(12); setFilterYear((y) => y - 1); }
    else setFilterMonth((m) => m - 1);
  };
  const goToNextMonth = () => {
    if (filterMonth === 12) { setFilterMonth(1); setFilterYear((y) => y + 1); }
    else setFilterMonth((m) => m + 1);
  };

  // ─── Actions ──────────────────────────────────────────────────────────────────
  const handleNewTransaction = async () => {
    if (isCheckingLimit) return;          // Guard: prevent double-tap
    setIsCheckingLimit(true);
    const check = await checkTransactionLimit();
    setIsCheckingLimit(false);
    if (!check.allowed) { setShowUpgrade(true); return; }
    setEditTransaction(null);
    setShowModal(true);
  };

  const handleSave = async (
    values: TransactionPayload
  ): Promise<{ success: boolean; message: string }> => {
    let result: { success: boolean; message: string };

    if (editTransaction) {
      result = await updateTransaction(editTransaction.id, values);
    } else if (!editTransaction && values.installments > 1) {
      // Batch installments creation
      const total = Number(values.amount);
      const parcelaBase = parseFloat((total / values.installments).toFixed(2));
      const groupId = crypto.randomUUID();
      const baseDate = new Date(values.date + 'T00:00:00');

      // Fix centavo loss: last installment = total − sum of all previous
      let sumSoFar = 0;
      const records: TransactionPayload[] = Array.from({ length: values.installments }, (_, i) => {
        const isLast = i === values.installments - 1;
        const amount = isLast
          ? parseFloat((total - sumSoFar).toFixed(2))
          : parcelaBase;
        if (!isLast) sumSoFar += parcelaBase;
        return {
          ...values,
          amount,
          installment_number: i + 1,
          installments: values.installments,
          installment_group_id: groupId,
          // Safe month addition: prevents overflow (e.g. Jan 31 → Feb 28, not Mar 3)
          date: addMonthsSafe(baseDate, i),
          description: `${values.description} (${i + 1}/${values.installments})`,
          status: i === 0 ? values.status : 'pendente',
        };
      });

      result = await createManyTransactions(records);
    } else {
      result = await createTransaction(values);
    }

    if (result.success) {
      showToast(result.message, 'success');
      await refreshCount();
    }
    return result;
  };

  /** Batch save for recurring records that already have correct descriptions and dates.
   *  Used by TransactionModal when recurring+count generates multiple pre-built records.
   *  DO NOT re-process descriptions or dates here — records come fully formed from buildRecurringRecords(). */
  const handleSaveMany = async (
    records: TransactionPayload[]
  ): Promise<{ success: boolean; message: string }> => {
    const result = await createManyTransactions(records);
    if (result.success) {
      showToast(result.message, 'success');
      await refreshCount();
    }
    return result;
  };

  const handleDelete = async (id: string) => {
    setActionLoading(id);
    const result = await deleteTransaction(id);
    setDeleteConfirm(null);
    setActionLoading(null);
    showToast(result.message, result.success ? 'success' : 'error');
    if (result.success) await refreshCount();
  };

  const handleDuplicate = async (t: Transaction) => {
    setActionLoading(`dup-${t.id}`);
    const result = await duplicateTransaction(t);
    setActionLoading(null);
    showToast(result.message, result.success ? 'success' : 'error');
    if (result.success) await refreshCount();
  };

  const handleToggleStatus = async (t: Transaction) => {
    const isMarkingAsPaid = !['pago', 'recebido'].includes(t.status);
    // Intercepta: despesa via conta sendo marcada como paga sem forma de pagamento
    if (isMarkingAsPaid && t.type === 'despesa' && t.payment_method === 'account' && !t.payment_method_detail) {
      setPendingPaymentTx(t);
      return;
    }
    setActionLoading(`status-${t.id}`);
    const result = await toggleStatus(t.id, t.status, t.type);
    setActionLoading(null);
    showToast(result.message, result.success ? 'success' : 'error');
  };

  const handleConfirmPaymentDetail = async (txId: string, detail: string) => {
    setPaymentDetailLoading(true);
    const today = new Date().toISOString().split('T')[0];
    const result = await updateTransaction(txId, { status: 'pago', payment_method_detail: detail, paid_at: today });
    setPaymentDetailLoading(false);
    setPendingPaymentTx(null);
    showToast(result.message, result.success ? 'success' : 'error');
    if (result.success) await refreshCount();
  };

  // ─── Filters ──────────────────────────────────────────────────────────────────
  const hasActiveFilters = !!(filterStatus || filterCategory || filterAccount || filterPaymentMethod || filterPayee || search);

  const clearFilters = () => {
    setFilterStatus(null);
    setFilterCategory(null);
    setFilterAccount(null);
    setFilterPaymentMethod(null);
    setFilterPayee(null);
    setSearch('');
  };

  const allCategories = Array.from(new Set(categories.map((c) => c.name))).sort();

  // ── Memoized filtering ─────────────────────────────────────────────────────
  // Evita recomputação em cada render quando só estado de UI muda.
  const filtered = useMemo(() => transactions
    .filter((t) => !search || (
      t.description.toLowerCase().includes(search.toLowerCase()) ||
      (t.category ?? '').toLowerCase().includes(search.toLowerCase()) ||
      (t.subcategory ?? '').toLowerCase().includes(search.toLowerCase()) ||
      ((t as Transaction & { payee_name?: string }).payee_name ?? '').toLowerCase().includes(search.toLowerCase())
    ))
    .filter((t) => !filterStatus        || t.status     === filterStatus)
    .filter((t) => !filterCategory      || t.category   === filterCategory)
    .filter((t) => !filterAccount       || t.account_id === filterAccount)
    .filter((t) => !filterPaymentMethod || (t as Transaction & { payment_method_detail?: string }).payment_method_detail === filterPaymentMethod)
    .filter((t) => !filterPayee         || (t as Transaction & { payee_id?: string }).payee_id === filterPayee),
    [transactions, search, filterStatus, filterCategory, filterAccount, filterPaymentMethod, filterPayee]
  );

  // ── Deduplicate transfer legs ──────────────────────────────────────────────
  // Memoizado: só recomputa quando `filtered` muda.
  const dedupedFiltered = useMemo(() => {
    const groupsWithDespesa = new Set(
      filtered
        .filter((t) =>
          (t as Transaction & { transaction_subtype?: string }).transaction_subtype === 'transferencia' &&
          t.type === 'despesa',
        )
        .map((t) => (t as Transaction & { transfer_group_id?: string }).transfer_group_id)
        .filter(Boolean) as string[],
    );
    return filtered.filter((t) => {
      const groupId = (t as Transaction & { transfer_group_id?: string }).transfer_group_id;
      const isTransfer = (t as Transaction & { transaction_subtype?: string }).transaction_subtype === 'transferencia';
      if (isTransfer && t.type === 'receita' && groupId && groupsWithDespesa.has(groupId)) return false;
      return true;
    });
  }, [filtered]);

  // ── Pagination ─────────────────────────────────────────────────────────────
  // Exibe PAGE_SIZE itens por vez. "Carregar mais" incrementa visibleCount.
  const PAGE_SIZE = 50;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  // Reset paginação ao mudar filtros ou mês
  const prevFilterKeyRef = useRef('');
  const filterKey = `${filterMonth}-${filterYear}-${filterType}-${filterStatus}-${filterCategory}-${filterAccount}-${filterPaymentMethod}-${filterPayee}-${search}`;
  if (prevFilterKeyRef.current !== filterKey) {
    prevFilterKeyRef.current = filterKey;
    if (visibleCount !== PAGE_SIZE) setVisibleCount(PAGE_SIZE);
  }

  const paginatedItems = useMemo(() => dedupedFiltered.slice(0, visibleCount), [dedupedFiltered, visibleCount]);
  const hasMore = visibleCount < dedupedFiltered.length;

  const isCurrentMonth =
    filterMonth === now.getMonth() + 1 && filterYear === now.getFullYear();

  return (
    <div className="space-y-6">
      {/* ─── Header ──────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-700 text-gray-900">Receitas &amp; Despesas</h1>
          <p className="text-sm text-gray-500 mt-0.5">Registre e acompanhe todos os seus lançamentos</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowCatManager(true)}
            title="Gerenciar categorias"
            className="w-10 h-10 flex items-center justify-center border border-gray-200 text-gray-500 hover:bg-gray-50 rounded-xl cursor-pointer transition-colors"
          >
            <i className="ri-price-tag-3-line text-base" />
          </button>
          <button
            onClick={handleNewTransaction}
            disabled={isCheckingLimit}
            className="flex items-center gap-2 px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-70 disabled:cursor-not-allowed text-white font-600 rounded-xl text-sm transition-colors cursor-pointer whitespace-nowrap"
          >
            {isCheckingLimit
              ? <i className="ri-loader-4-line animate-spin" />
              : <i className="ri-add-line" />}
            Novo Lançamento
            {isCurrentMonth && monthlyTransactionLimit !== null && (
              <span className="ml-1 text-emerald-200 text-xs">({transactionCount}/{monthlyTransactionLimit})</span>
            )}
          </button>
        </div>
      </div>

      {isCurrentMonth && monthlyTransactionLimit !== null && (
        <PlanLimitAlert
          current={transactionCount}
          limit={monthlyTransactionLimit}
          resource="lançamentos este mês"
          onUpgrade={() => setShowUpgrade(true)}
        />
      )}

      {/* ─── Stats ───────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { icon: 'ri-arrow-up-line',   bg: 'bg-emerald-50', ic: 'text-emerald-500', label: 'Receitas realizadas',  val: realizedIncome,              vc: 'text-emerald-600', sub: 'entradas com status recebido' },
          { icon: 'ri-arrow-down-line', bg: 'bg-red-50',     ic: 'text-red-500',     label: 'Despesas pagas',       val: realizedExpenses,             vc: 'text-red-500',     sub: 'saídas com status pago' },
          { icon: 'ri-scales-line',
            bg: realizedIncome - realizedExpenses >= 0 ? 'bg-emerald-50' : 'bg-red-50',
            ic: realizedIncome - realizedExpenses >= 0 ? 'text-emerald-500' : 'text-red-500',
            label: 'Saldo realizado', val: realizedIncome - realizedExpenses,
            vc: realizedIncome - realizedExpenses >= 0 ? 'text-emerald-600' : 'text-red-500',
            sub: 'apenas lançamentos pagos/recebidos' },
        ].map((c, i) => (
          <div key={i} className="bg-white rounded-2xl border border-gray-100 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-7 h-7 ${c.bg} rounded-lg flex items-center justify-center`}>
                <i className={`${c.icon} ${c.ic} text-sm`} />
              </div>
              <span className="text-xs text-gray-400 font-500">{c.label}</span>
            </div>
            <p className={`text-lg font-700 ${c.vc}`}>{formatCurrency(c.val)}</p>
            <p className="text-[10px] text-gray-400 mt-0.5">{c.sub}</p>
          </div>
        ))}
      </div>

      {/* ─── Consolidated context strip ──────────────────────────────── */}
      {accounts.length > 0 && (
        <div className="bg-gray-50 rounded-xl border border-gray-100 px-4 py-3">
          <div className="flex items-center gap-2">
            <i className="ri-bank-line text-gray-400 text-xs" />
            <span className="text-[11px] font-600 text-gray-400 uppercase tracking-wide">
              Situação atual das contas
            </span>
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-[11px] text-gray-400">{accounts.length} conta{accounts.length !== 1 ? 's' : ''}</span>
            <button
              onClick={() => setShowAccountStrip((v) => !v)}
              title={showAccountStrip ? 'Ocultar resumo' : 'Exibir resumo'}
              className="w-6 h-6 flex items-center justify-center rounded-lg hover:bg-gray-200 text-gray-400 hover:text-gray-600 cursor-pointer transition-colors"
            >
              <i className={`text-sm ${showAccountStrip ? 'ri-eye-off-line' : 'ri-eye-line'}`} />
            </button>
          </div>
          {showAccountStrip && (
            <div className="grid grid-cols-3 gap-4 mt-3">
              <div>
                <p className="text-[11px] text-gray-400 font-500 mb-0.5">Saldo disponível</p>
                <p className={`text-sm font-700 ${totalBalance >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                  {formatCurrency(totalBalance)}
                </p>
                <p className="text-[10px] text-gray-400 mt-0.5">soma de todas as contas</p>
              </div>
              <div>
                <p className="text-[11px] text-gray-400 font-500 mb-0.5">Pendente a pagar</p>
                <p className="text-sm font-700 text-amber-600">
                  {pendingExpenses > 0 ? `- ${formatCurrency(pendingExpenses)}` : '—'}
                </p>
                <p className="text-[10px] text-gray-400 mt-0.5">despesas não pagas no período</p>
              </div>
              <div>
                <p className="text-[11px] text-gray-400 font-500 mb-0.5">Pendente a receber</p>
                <p className="text-sm font-700 text-teal-600">
                  {pendingIncome > 0 ? `+ ${formatCurrency(pendingIncome)}` : '—'}
                </p>
                <p className="text-[10px] text-gray-400 mt-0.5">receitas ainda não confirmadas</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── Filters ─────────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-100 p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          {/* Month nav */}
          <div className="flex items-center gap-1 bg-gray-50 rounded-xl p-1">
            <button onClick={goToPrevMonth} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-white text-gray-500 cursor-pointer">
              <i className="ri-arrow-left-s-line" />
            </button>
            <span className="text-sm font-600 text-gray-700 px-2 min-w-[90px] text-center">
              {MONTH_NAMES[filterMonth - 1]} {filterYear}
            </span>
            <button onClick={goToNextMonth} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-white text-gray-500 cursor-pointer">
              <i className="ri-arrow-right-s-line" />
            </button>
          </div>

          {/* Type filter */}
          <div className="flex items-center gap-1 bg-gray-50 rounded-xl p-1">
            {([null, 'receita', 'despesa'] as const).map((t) => (
              <button
                key={t ?? 'all'}
                onClick={() => setFilterType(t)}
                className={`px-3 py-1.5 rounded-lg text-xs font-600 transition-all cursor-pointer whitespace-nowrap ${
                  filterType === t ? 'bg-white text-gray-900' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {t === null ? 'Todos' : t === 'receita' ? 'Receitas' : 'Despesas'}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="flex-1 min-w-[180px]">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por descrição ou categoria..."
                className="w-full pl-8 pr-8 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-emerald-400 bg-gray-50"
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer">
                  <i className="ri-close-line text-sm" />
                </button>
              )}
            </div>
          </div>

          {/* Toggle advanced filters */}
          <button
            onClick={() => setShowAdvanced((v) => !v)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-600 border transition-all cursor-pointer whitespace-nowrap ${
              showAdvanced || hasActiveFilters
                ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
                : 'border-gray-200 text-gray-500 hover:border-gray-300'
            }`}
          >
            <i className="ri-filter-3-line text-sm" />
            Filtros
            {hasActiveFilters && (
              <span className="w-4 h-4 flex items-center justify-center bg-emerald-500 text-white rounded-full text-[10px]">
                {[filterStatus, filterCategory, filterAccount, filterPaymentMethod].filter(Boolean).length + (search ? 1 : 0)}
              </span>
            )}
          </button>
        </div>

        {/* Advanced filter row */}
        {showAdvanced && (
          <div className="flex flex-wrap items-center gap-3 pt-1">
            {/* Status filter - scrollable on mobile */}
            <div className="overflow-x-auto pb-1 -mb-1">
              <div className="flex items-center gap-1 bg-gray-50 rounded-xl p-1 w-max">
                {([null, 'pago', 'recebido', 'pendente', 'vencido', 'atrasado'] as const).map((s) => (
                  <button
                    key={s ?? 'all-st'}
                    onClick={() => setFilterStatus(s)}
                    className={`px-2.5 py-1.5 rounded-lg text-xs font-600 transition-all cursor-pointer whitespace-nowrap ${
                      filterStatus === s ? 'bg-white text-gray-900' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {s === null ? 'Todos status' : STATUS_CONFIG[s]?.label ?? s}
                  </button>
                ))}
              </div>
            </div>

            {/* Account filter */}
            {accounts.length > 0 && (
              <select
                value={filterAccount ?? ''}
                onChange={(e) => setFilterAccount(e.target.value || null)}
                className="px-3 py-2 border border-gray-200 rounded-xl text-xs font-600 text-gray-600 focus:outline-none focus:border-emerald-400 bg-white cursor-pointer"
              >
                <option value="">Todas as contas</option>
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            )}

            {/* Category filter */}
            <select
              value={filterCategory ?? ''}
              onChange={(e) => setFilterCategory(e.target.value || null)}
              className="px-3 py-2 border border-gray-200 rounded-xl text-xs font-600 text-gray-600 focus:outline-none focus:border-emerald-400 bg-white cursor-pointer"
            >
              <option value="">Todas as categorias</option>
              {allCategories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>

            {/* Payment method filter — only relevant for account payments */}
            <select
              value={filterPaymentMethod ?? ''}
              onChange={(e) => setFilterPaymentMethod(e.target.value || null)}
              className="px-3 py-2 border border-gray-200 rounded-xl text-xs font-600 text-gray-600 focus:outline-none focus:border-emerald-400 bg-white cursor-pointer"
            >
              <option value="">Qualquer forma de pagamento</option>
              {PAYMENT_METHOD_MODAL_OPTIONS.map((pm) => (
                <option key={pm.value} value={pm.value}>{pm.label}</option>
              ))}
            </select>

            {/* Payee filter */}
            {activePayees.length > 0 && (
              <select
                value={filterPayee ?? ''}
                onChange={(e) => setFilterPayee(e.target.value || null)}
                className="px-3 py-2 border border-gray-200 rounded-xl text-xs font-600 text-gray-600 focus:outline-none focus:border-emerald-400 bg-white cursor-pointer"
              >
                <option value="">Todos os favorecidos</option>
                {activePayees.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            )}

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-600 text-red-500 hover:bg-red-50 border border-red-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-close-circle-line text-sm" />
                Limpar filtros
              </button>
            )}
          </div>
        )}
      </div>

      {/* ─── Error ───────────────────────────────────────────────────── */}
      {error && (
        <div className="flex items-center gap-3 bg-red-50 text-red-700 text-sm px-4 py-3 rounded-xl border border-red-100">
          <i className="ri-error-warning-line shrink-0" />
          {error}
        </div>
      )}

      {/* ─── List ────────────────────────────────────────────────────── */}
      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse" />)}
        </div>
      ) : dedupedFiltered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-14 h-14 bg-emerald-50 rounded-2xl flex items-center justify-center mb-4">
            <i className="ri-exchange-dollar-line text-2xl text-emerald-400" />
          </div>
          <p className="text-base font-600 text-gray-700 mb-1">
            {hasActiveFilters || search ? 'Nenhum resultado encontrado' : 'Sem lançamentos neste período'}
          </p>
          <p className="text-sm text-gray-400 mb-5">
            {hasActiveFilters
              ? 'Tente ajustar os filtros ou limpar a busca.'
              : `Nenhum lançamento em ${MONTH_NAMES[filterMonth - 1]} de ${filterYear}.`}
          </p>
          {hasActiveFilters ? (
            <button
              onClick={clearFilters}
              className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 text-gray-600 font-600 rounded-xl text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-close-circle-line" />
              Limpar filtros
            </button>
          ) : (
            <button
              onClick={handleNewTransaction}
              className="flex items-center gap-2 px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white font-600 rounded-xl text-sm cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line" />
              Registrar lançamento
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="divide-y divide-gray-50">
            {paginatedItems.map((t) => {
              const isTransfer = (t as Transaction & { transaction_subtype?: string }).transaction_subtype === 'transferencia';
              const isStatusLoad = actionLoading === `status-${t.id}`;
              const isDupLoad    = actionLoading === `dup-${t.id}`;
              const accountName =
                t.payment_method === 'card'
                  ? (cardMap[t.card_id ?? '']?.name ?? null)
                  : (accountMap[t.account_id ?? '']?.name ?? null);

              return (
                <div key={t.id} className="flex items-center gap-3 px-4 py-3.5 hover:bg-gray-50 transition-colors group">
                  {/* Icon */}
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${isTransfer ? 'bg-teal-50' : t.type === 'receita' ? 'bg-emerald-50' : 'bg-red-50'}`}>
                    <i className={`text-base ${isTransfer ? 'ri-arrow-left-right-line text-teal-500' : t.type === 'receita' ? 'ri-arrow-up-line text-emerald-500' : 'ri-arrow-down-line text-red-400'}`} />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-600 text-gray-900 truncate">{t.description}</p>
                      {isTransfer && (
                        <span className="text-[10px] bg-teal-50 text-teal-600 border border-teal-100 px-1.5 py-0.5 rounded-lg font-600 whitespace-nowrap">Transferência / PIX</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      {t.category && <span className="text-xs text-gray-400">{t.category}</span>}
                      {accountName && (
                        <>
                          <span className="text-gray-200">·</span>
                          <span className="text-xs text-gray-400 flex items-center gap-0.5">
                            <i className={`${t.payment_method === 'card' ? 'ri-bank-card-line' : 'ri-bank-line'} text-[10px]`} />
                            {accountName}
                          </span>
                        </>
                      )}
                      {(t as Transaction & { payment_method_detail?: string }).payment_method_detail && (
                        <>
                          <span className="text-gray-200">·</span>
                          <span className="text-xs text-emerald-600 font-500 flex items-center gap-0.5">
                            <i className="ri-checkbox-circle-line text-[10px]" />
                            via {PAYMENT_METHOD_LABELS[(t as Transaction & { payment_method_detail?: string }).payment_method_detail!] ?? (t as Transaction & { payment_method_detail?: string }).payment_method_detail}
                          </span>
                        </>
                      )}
                      {(t.installments ?? 1) > 1 && (
                        <>
                          <span className="text-gray-200">·</span>
                          <span className="text-xs text-gray-400">{t.installment_number}/{t.installments}x</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Amount + date + status */}
                  <div className="text-right shrink-0">
                    <p className={`text-sm font-700 ${
                      t.type === 'despesa'
                        ? 'text-red-500'
                        : isTransfer
                          ? 'text-teal-600'
                          : 'text-emerald-600'
                    }`}>
                      {t.type === 'despesa' ? '- ' : '+ '}{formatCurrency(t.amount)}
                    </p>
                    <div className="flex items-center gap-1.5 justify-end mt-0.5 flex-wrap">
                      {/* Data de pagamento real (quando pago antecipado/atrasado) */}
                      {(t as Transaction & { paid_at?: string | null }).paid_at &&
                       (t as Transaction & { paid_at?: string | null }).paid_at !== t.date ? (
                        <span className="text-xs text-emerald-600 hidden sm:inline" title={`Vencimento: ${new Date(t.date + 'T00:00:00').toLocaleDateString('pt-BR')}`}>
                          <i className="ri-check-double-line mr-0.5" />
                          {new Date((t as Transaction & { paid_at?: string | null }).paid_at! + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400 hidden sm:inline">
                          {new Date(t.date + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })}
                        </span>
                      )}
                      <span className={`text-xs px-1.5 py-0.5 rounded-lg font-500 ${STATUS_CONFIG[t.status]?.className ?? 'bg-gray-50 text-gray-500'}`}>
                        {STATUS_CONFIG[t.status]?.label ?? t.status}
                      </span>
                    </div>
                  </div>

                  {/* Actions — fixed width so amounts stay aligned regardless of button count */}
                  <div className="flex items-center gap-1 shrink-0 w-[88px] justify-end">
                    {/* Toggle status — placeholder for transfers to keep alignment */}
                    {!isTransfer ? (
                      <button title={['pago', 'recebido'].includes(t.status) ? 'Marcar como pendente' : 'Marcar como pago'} onClick={() => handleToggleStatus(t)} disabled={!!actionLoading}
                        className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-300 hover:bg-emerald-50 hover:text-emerald-600 cursor-pointer disabled:opacity-50 transition-colors">
                        {isStatusLoad ? <i className="ri-loader-4-line animate-spin text-sm" /> : <i className={`text-sm ${['pago', 'recebido'].includes(t.status) ? 'ri-checkbox-circle-line' : 'ri-time-line'}`} />}
                      </button>
                    ) : (
                      <span className="w-7 h-7 shrink-0" />
                    )}

                    {/* Edit — hidden for transfers */}
                    {!isTransfer ? (
                      <button title="Editar" onClick={() => { setEditTransaction(t); setShowModal(true); }}
                        className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-300 hover:bg-gray-100 hover:text-gray-600 cursor-pointer transition-colors">
                        <i className="ri-edit-line text-sm" />
                      </button>
                    ) : (
                      <span className="w-7 h-7 shrink-0" />
                    )}

                    {/* Delete */}
                    <button title="Excluir"
                      onClick={() => isTransfer && (t as Transaction & { transfer_group_id?: string }).transfer_group_id
                        ? setDeleteTransferConfirm((t as Transaction & { transfer_group_id?: string }).transfer_group_id ?? null)
                        : setDeleteConfirm(t.id)}
                      className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-300 hover:bg-red-50 hover:text-red-500 cursor-pointer transition-colors">
                      <i className="ri-delete-bin-line text-sm" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="px-5 py-3 border-t border-gray-100 bg-gray-50/50 space-y-3">
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-400">
                {paginatedItems.length} de {dedupedFiltered.length} lançamento{dedupedFiltered.length !== 1 ? 's' : ''}
                {dedupedFiltered.length !== transactions.length && ` (de ${transactions.length} no período)`}
              </p>
              {hasActiveFilters && (
                <button onClick={clearFilters} className="text-xs text-emerald-600 font-600 hover:underline cursor-pointer whitespace-nowrap">
                  Limpar filtros
                </button>
              )}
            </div>
            {hasMore && (
              <button
                onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                className="w-full py-2.5 border border-gray-200 hover:border-emerald-300 hover:bg-emerald-50 text-gray-500 hover:text-emerald-700 text-xs font-600 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-down-line mr-1.5" />
                Carregar mais ({dedupedFiltered.length - visibleCount} restantes)
              </button>
            )}
          </div>
        </div>
      )}

      {/* ─── Modals ───────────────────────────────────────────────────── */}
      <TransactionModal
        key={editTransaction?.id ?? 'new'}
        open={showModal}
        transaction={editTransaction}
        onClose={() => { setShowModal(false); setEditTransaction(null); }}
        onSave={handleSave}
        onSaveMany={handleSaveMany}
      />

      <CategoryManager open={showCatManager} onClose={() => setShowCatManager(false)} />

      <PaymentDetailModal
        open={!!pendingPaymentTx}
        transaction={pendingPaymentTx}
        accountName={pendingPaymentTx?.account_id ? accountMap[pendingPaymentTx.account_id]?.name : undefined}
        loading={paymentDetailLoading}
        onClose={() => setPendingPaymentTx(null)}
        onConfirm={handleConfirmPaymentDetail}
      />

      <UpgradeModal
        open={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        title="Limite de lançamentos atingido"
        description={`O plano Free permite apenas ${monthlyTransactionLimit ?? 30} lançamentos por mês. Faça upgrade para registrar sem limites.`}
      />

      {/* Delete transfer confirm */}
      {deleteTransferConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setDeleteTransferConfirm(null)} />
          <div className="relative bg-white rounded-2xl max-w-sm w-full mx-4 p-6 text-center">
            <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-arrow-left-right-line text-teal-500 text-xl" />
            </div>
            <h3 className="text-base font-700 text-gray-900 mb-2">Excluir transferência?</h3>
            <p className="text-sm text-gray-500 mb-5">Os 2 lançamentos vinculados serão excluídos e os saldos serão ajustados. Essa ação não pode ser desfeita.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteTransferConfirm(null)} className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-600 rounded-xl text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap">Cancelar</button>
              <button
                onClick={async () => {
                  setActionLoading(deleteTransferConfirm ?? 'transfer');
                  const result = await deleteTransfer(deleteTransferConfirm);
                  setDeleteTransferConfirm(null);
                  setActionLoading(null);
                  showToast(result.message, result.success ? 'success' : 'error');
                }}
                className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white font-600 rounded-xl text-sm cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                {!!actionLoading && <i className="ri-loader-4-line animate-spin text-sm" />}
                Excluir ambos
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setDeleteConfirm(null)} />
          <div className="relative bg-white rounded-2xl max-w-sm w-full mx-4 p-6 text-center">
            <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-delete-bin-line text-red-500 text-xl" />
            </div>
            <h3 className="text-base font-700 text-gray-900 mb-2">Excluir lançamento?</h3>
            <p className="text-sm text-gray-500 mb-5">Essa ação não pode ser desfeita.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-600 rounded-xl text-sm cursor-pointer whitespace-nowrap hover:bg-gray-50">
                Cancelar
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                disabled={actionLoading === deleteConfirm}
                className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 disabled:opacity-60 text-white font-600 rounded-xl text-sm cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                {actionLoading === deleteConfirm && <i className="ri-loader-4-line animate-spin text-sm" />}
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl font-500 text-sm ${toast.type === 'success' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'}`}>
          <i className={toast.type === 'success' ? 'ri-checkbox-circle-line text-base' : 'ri-error-warning-line text-base'} />
          {toast.message}
        </div>
      )}
    </div>
  );
}
